/**
 * API keys — the decisions, kept pure.
 *
 * A key is a credential a person pastes into a script and then forgets about for two years. That
 * one sentence is where every rule below comes from: it will be committed to a repository, printed
 * in a log, mailed to a colleague and left behind by the person who made it. So a key is
 * **read-only unless somebody deliberately says otherwise**, it **carries a role well below the
 * owner's**, and it **can be given an end date at the moment it is created**, which is the only
 * moment anybody is thinking about it.
 *
 * Everything here is arithmetic and string handling — no database, no clock beyond what is handed
 * in — so the awkward cases are tested in microseconds. Where a key is *stored* and how it is
 * resolved is `apps/api/src/lib/api-keys.ts`.
 */

import type { Role } from "./permissions";
import type { ThrottleRule } from "./throttle";

/**
 * What a key is allowed to do.
 *
 * Two, not six. A scope list long enough to be expressive is a scope list nobody reads before
 * ticking every box, and the distinction that actually matters — can this thing change our books
 * or only report on them — is the first one. `read` is the default everywhere, including for a
 * key whose stored scopes are missing or unrecognised.
 */
export const API_KEY_SCOPES = ["read", "write"] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

/**
 * The prefix every key carries.
 *
 * Two jobs. It tells `sessionFrom()` that a bearer token is a key rather than a session without a
 * database round trip, so the two credentials can share one header and never be confused for one
 * another. And it is what secret scanners match on: a string that announces what it is can be
 * found in a public repository by a machine, which is the only way a leaked key is ever noticed
 * before it is used.
 */
export const API_KEY_PREFIX = "rnk_";

/**
 * Whether we have said this key may work.
 *
 * **A key is a request before it is a credential.** The agency mints it, is shown the secret once,
 * and then it waits until somebody on our side has looked at it. That is the platform owner's
 * instruction, and it is why this axis is separate from the scope: the scope is what the agency
 * asked for, this is what we answered.
 *
 * Three states and no fourth. `declined` is deliberately terminal — see `apiKeyStatus`.
 */
export const API_KEY_APPROVALS = ["pending", "approved", "declined"] as const;
export type ApiKeyApproval = (typeof API_KEY_APPROVALS)[number];

/** A stored key row, as much of it as any decision here needs. */
export interface ApiKeyRecord {
  scopes?: string[] | null;
  revokedAt?: Date | string | null;
  expiresAt?: Date | string | null;
  /** `pending` | `approved` | `declined`. Anything else, absent included, is treated as pending. */
  approvalState?: string | null;
}

/**
 * What we actually answered, for a row that may say anything.
 *
 * **Anything unrecognised — a missing value included — is `pending`, which authenticates nothing.**
 * That is the opposite direction from `apiKeyScope`, and for exactly the same reason: both fail
 * towards the harmless answer. A scope we cannot read must not turn out to be the powerful one; an
 * approval we cannot read must not turn out to be the one that lets a credential through.
 *
 * The column is `not null default 'approved'` and the migration backfills every key that existed
 * before moderation did, so a live key never arrives here with nothing to read. A row that somehow
 * does is one we cannot vouch for, and the right place for a credential we cannot vouch for is the
 * queue.
 */
export function apiKeyApproval(state: string | null | undefined): ApiKeyApproval {
  return state === "approved" || state === "declined" ? state : "pending";
}

/**
 * Which scope a stored row actually grants.
 *
 * **Anything unrecognised resolves to `read`.** A row whose scopes are null — every key written
 * before this column meant anything — must not be the most powerful kind of key in the product by
 * accident, and a scope added later and then removed must not silently widen the keys that still
 * name it.
 */
export function apiKeyScope(scopes: string[] | null | undefined): ApiKeyScope {
  return scopes?.includes("write") ? "write" : "read";
}

/**
 * The role a key acts with.
 *
 * **Deliberately not the role of whoever created it.** Only an owner or an administrator may mint
 * a key — they are the two roles holding `api.manage` — and a credential that could do everything
 * an owner can do is one that could change the plan, invite staff, delete the agency and mint more
 * keys, from inside a script, with nobody watching. So:
 *
 * - `read`  → **viewer**, which holds only the `.view` permissions.
 * - `write` → **manager**, which runs the portfolio — properties, tenancies, invoices, payments,
 *   reminders — and holds none of `org.settings`, `org.members`, `org.billing`, `org.delete` or
 *   `api.manage`. A stolen write key cannot lock the agency out of its own account, cannot move
 *   where the money is paid, and cannot mint a replacement for itself.
 *
 * The read-only case is enforced twice over, here by the role and again by the request method in
 * `apps/api/src/lib/api-keys.ts` — because a good many endpoints in this product declare no
 * permission at all and then write, which is the same trap the suspension check fell into.
 */
export function roleForApiKeyScope(scope: ApiKeyScope): Role {
  return scope === "write" ? "manager" : "viewer";
}

/** True when a bearer token is a key rather than a session token. */
export function looksLikeApiKey(token: string): boolean {
  return token.startsWith(API_KEY_PREFIX);
}

/** The whole credential as the person is shown it, once. */
export function formatApiKey(secret: string): string {
  return `${API_KEY_PREFIX}${secret}`;
}

/**
 * The last four characters, kept so a key can be pointed at in a list.
 *
 * Four is enough to match against what is pasted in a configuration file and far too little to
 * narrow a guess: the secret is 256 bits, and knowing four of its base64 characters leaves 232.
 */
export function apiKeyHint(key: string): string {
  return key.slice(-4);
}

/** How long a key may be given to live. `null` is "until it is revoked". */
export const API_KEY_LIFETIMES = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "One year" },
  { days: null, label: "No end date" },
] as const;

/**
 * When a key created now should stop working.
 *
 * An end date is offered at creation because that is the only moment anybody is thinking about the
 * key at all. Nobody returns to a settings screen to retire a credential that is working.
 */
export function apiKeyExpiry(days: number | null | undefined, from: Date = new Date()): Date | null {
  if (days === null || days === undefined) return null;
  if (!Number.isFinite(days) || days <= 0) return null;
  return new Date(from.getTime() + days * 86_400_000);
}

export type ApiKeyStatus = "active" | "revoked" | "expired" | "pending" | "declined";

/**
 * What a key is, right now — **both axes, in one answer.**
 *
 * There is deliberately no function that reports only the lifecycle. Two things decide whether a
 * key works: what has happened to it since it was made (revoked, lapsed), and whether we ever said
 * yes to it. A helper answering half of that is a helper somebody calls at the door, and what it
 * would let through is a credential nobody approved authenticating as an agency. One function,
 * five answers, and every caller gets the whole truth.
 *
 * The order is the order of finality:
 *
 * 1. **Revoked** beats everything. A key somebody deliberately killed should say so, even if it
 *    would have lapsed on its own the following week.
 * 2. **Declined** beats the rest, and is terminal. A key we refused must never become live later —
 *    not by being approved afterwards, not by moderation being switched off. It is refused here,
 *    and `routes/platform.ts` separately refuses to reconsider one; two locks, because this is the
 *    state whose failure hands somebody a credential we looked at and said no to.
 * 3. **Pending** beats expired, because "waiting" is the true and useful thing to tell an agency
 *    about a key that has never worked. A key left in the queue past its own end date is approved
 *    into an expired key and refused as expired on its first call — honest and diagnosable, and
 *    the reason the console queue shows how long each request has been waiting.
 */
export function apiKeyStatus(key: ApiKeyRecord, now: Date = new Date()): ApiKeyStatus {
  if (key.revokedAt) return "revoked";
  const approval = apiKeyApproval(key.approvalState);
  if (approval === "declined") return "declined";
  if (approval === "pending") return "pending";
  const expires = toDate(key.expiresAt);
  if (expires && expires.getTime() <= now.getTime()) return "expired";
  return "active";
}

/** The one question authentication asks. */
export function isApiKeyUsable(key: ApiKeyRecord, now: Date = new Date()): boolean {
  return apiKeyStatus(key, now) === "active";
}

/**
 * What state a key created *right now* is born in.
 *
 * One line in one place, so the endpoint that mints a key and the screen that warns about it
 * cannot disagree. `moderated` is the platform setting — on by default, and an operator can turn
 * it off without a deploy.
 */
export function apiKeyOnCreation(moderated: boolean): ApiKeyApproval {
  return moderated ? "pending" : "approved";
}

/**
 * What the agency is told about a key that is not live, in the words their own screen uses.
 *
 * The same shape and the same job as `describeReviewState` in `review.ts`, which is the nearest
 * prior art in this product: a state, an optional note somebody on our side wrote, and two
 * sentences the agency can act on. An active key returns null — there is nothing to say about a
 * key that works.
 *
 * **A key waiting for us is never described as invalid.** The agency created it deliberately, a
 * minute ago, and telling them it is wrong is a support ticket we wrote ourselves.
 */
export function describeApiKeyApproval(
  status: ApiKeyStatus,
  note?: string | null,
): { headline: string; detail: string } | null {
  if (status === "pending") {
    return {
      headline: "This key is waiting to be approved.",
      detail:
        "It was created and the secret is yours to keep. It authenticates nothing until it has been " +
        "checked, and it starts working the moment it is approved. Nothing further is needed from you.",
    };
  }
  if (status === "declined") {
    return {
      headline: "This key was not approved.",
      detail: note?.trim()
        ? `${note.trim()} A key that was not approved cannot be brought back — create another once that is settled.`
        : "A key that was not approved cannot be brought back. Create another once that is settled.",
    };
  }
  return null;
}

/** What each state is called, and what it means, in the words both screens use. */
export const API_KEY_STATUS_COPY: Record<ApiKeyStatus, { label: string; detail: string }> = {
  active: { label: "Active", detail: "Approved, and working." },
  pending: { label: "Awaiting approval", detail: "Created, and waiting to be checked before it works." },
  declined: { label: "Not approved", detail: "It was looked at and refused. It will never work." },
  expired: { label: "Expired", detail: "It passed the end date it was given when it was created." },
  revoked: { label: "Revoked", detail: "Withdrawn by the agency. It stopped working at once." },
};

/**
 * What one key may ask for.
 *
 * **Keyed on the key, not on the source and not on the agency.** The source is `x-forwarded-for`,
 * which is forgeable where nothing sets it — useless against a script that can choose its own
 * header. The agency is too coarse: an agency with a nightly export and a live webhook consumer
 * would have one starve the other, and the runaway one is precisely the one that wins that race.
 * A key id is issued by us, unforgeable and exactly as granular as the thing being limited, so a
 * loop in one integration cannot slow down another key belonging to the same agency.
 *
 * **This is a quota, not an anti-abuse limit**, which is why it exists at all when ADR-0084 says
 * nothing authenticated is throttled. That rule was written about a busy office, and it is right:
 * forty people recording payments is not an attack. A credential in a machine is a different
 * animal — the failure mode is a retry loop with no sleep in it, running all night, degrading the
 * API for every other agency in the process. Nobody is at the keyboard to notice.
 *
 * Three hundred a minute is five a second sustained. A full synchronisation of the largest
 * portfolio a plan with API access covers — six hundred units, one request each — finishes inside
 * two minutes and is never refused; a loop with no delay in it spends the allowance in under a
 * second and is told, by `Retry-After`, exactly when to come back.
 */
export const API_KEY_THROTTLE: ThrottleRule = { limit: 300, windowSeconds: 60 };

/** What each scope is called and what it means, in the words the settings screen uses. */
export const API_KEY_SCOPE_COPY: Record<ApiKeyScope, { label: string; detail: string }> = {
  read: {
    label: "Read only",
    detail: "Retrieves records. It cannot create, change or delete anything.",
  },
  write: {
    label: "Read and write",
    detail:
      "Retrieves records and may also create and change them. It cannot alter your settings, " +
      "your team, your billing or your keys.",
  },
};

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  return value instanceof Date ? value : new Date(value);
}
